import React from "react";

/**
 * ContactMotif — decorative SVG accent used on the Contact hero.
 * A schematic "signal" motif: three concentric dashed arcs radiating
 * from a single anchor node, suggesting outbound communication.
 * Monochrome, hairline grammar. Two node dots pulse subtly.
 */
export default function ContactMotif({ className = "" }) {
  return (
    <svg
      viewBox="0 0 380 380"
      className={className}
      role="presentation"
      aria-hidden="true"
    >
      <defs>
        <radialGradient id="mtsContactHaze" cx="30%" cy="70%" r="55%">
          <stop offset="0%" stopColor="#0a0a0a" stopOpacity="0.05" />
          <stop offset="100%" stopColor="#0a0a0a" stopOpacity="0" />
        </radialGradient>
        <pattern
          id="mtsContactDots"
          width="14"
          height="14"
          patternUnits="userSpaceOnUse"
        >
          <circle cx="1" cy="1" r="1" fill="#0a0a0a" fillOpacity="0.04" />
        </pattern>
        <mask id="mtsContactFade">
          <radialGradient id="mtsContactFadeGrad" cx="30%" cy="70%" r="55%">
            <stop offset="0%" stopColor="#ffffff" stopOpacity="1" />
            <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
          </radialGradient>
          <rect width="380" height="380" fill="url(#mtsContactFadeGrad)" />
        </mask>
      </defs>

      {/* dot texture */}
      <g mask="url(#mtsContactFade)">
        <rect width="380" height="380" fill="url(#mtsContactDots)" />
      </g>

      {/* radial haze */}
      <circle cx="115" cy="265" r="180" fill="url(#mtsContactHaze)" />

      {/* concentric dashed arcs from anchor node */}
      {[80, 140, 200, 260].map((r, i) => (
        <circle
          key={r}
          cx="115"
          cy="265"
          r={r}
          fill="none"
          stroke="#0a0a0a"
          strokeOpacity={0.16 - i * 0.02}
          strokeDasharray="2 8"
        />
      ))}

      {/* satellite dots — receiving endpoints */}
      {[
        { x: 275, y: 130 },
        { x: 320, y: 205 },
        { x: 235, y: 88 },
        { x: 148, y: 60 },
      ].map((p) => (
        <g key={`sat-${p.x}-${p.y}`}>
          <circle
            cx={p.x}
            cy={p.y}
            r="7"
            fill="#ffffff"
            stroke="#0a0a0a"
            strokeOpacity="0.22"
          />
          <circle cx={p.x} cy={p.y} r="2.5" fill="#0a0a0a" />
        </g>
      ))}

      {/* connecting hairlines to two of the satellites */}
      {[
        { x: 275, y: 130 },
        { x: 235, y: 88 },
      ].map((p) => (
        <line
          key={`ln-${p.x}`}
          x1="115"
          y1="265"
          x2={p.x}
          y2={p.y}
          stroke="#0a0a0a"
          strokeOpacity="0.14"
        />
      ))}

      {/* anchor node — solid, pulsing */}
      <g>
        <circle cx="115" cy="265" r="24" fill="#ffffff" stroke="#e5e5ea" />
        <circle cx="115" cy="265" r="16" fill="#0a0a0a" />
        <circle cx="115" cy="265" r="6" fill="none" stroke="#ffffff" strokeOpacity="0.35" />
        <circle cx="115" cy="265" r="16" fill="none" stroke="#0a0a0a" strokeOpacity="0.4">
          <animate attributeName="r" values="16;48;16" dur="4s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.4;0;0.4" dur="4s" repeatCount="indefinite" />
        </circle>
      </g>
    </svg>
  );
}
